import moment from "moment";
import DOMPurify from "dompurify";

import { saveData, getData, removeData } from "./storage";
import {
  Functionalities,
  PATIENT_PORTAL_AUTO_LOGIN_API_KEY
} from "../constants";
import {
  getBaseUrl,
  getPatientOrigin,
  getPgidFromUrl
} from "../ApplicationSettings";

export const storeTokenData = (pgid, tokenDataString) => {
  if (pgid) {
    saveData(
      Functionalities.TokenDataKey + "_" + pgid + "_" + getPatientOrigin(),
      tokenDataString
    );
  }
};

export const storeSelectedPatientId = (patId) => {
  if (patId) {
    let patientIdKey = generateOriginBasedKey(
      Functionalities.SelectedPatientId
    );
    saveData(patientIdKey, patId);
  }
};

export const storeNavigationBarHiddenValue = (hideNavigationBar) => {
  if (hideNavigationBar) {
    let hideNavbarKey = generateOriginBasedKey(
      Functionalities.ShouldHideNavbar
    );
    saveData(hideNavbarKey, hideNavigationBar);
  }
};

export const storeShouldLogOutAndCloseTabAfterSmartScanCreateValue = (
  shouldLogOutAndClose
) => {
  if (shouldLogOutAndClose) {
    let logOutAndCloseKey = generateOriginBasedKey(
      Functionalities.ShouldLogOutAndCloseTabAfterSmartScanCreate
    );

    saveData(logOutAndCloseKey, shouldLogOutAndClose);
  }
};

export const storeShouldLogOutAndCloseTabAfterPhoneCallRequestValue = (
  shouldLogOutAndClose
) => {
  if (shouldLogOutAndClose) {
    let logOutAndCloseKey = generateOriginBasedKey(
      Functionalities.ShouldLogOutAndCloseTabAfterPhoneCallRequest
    );

    saveData(logOutAndCloseKey, shouldLogOutAndClose);
  }
};

export const shouldLogOutAndCloseTabAfterSmartScanCreate = () => {
  const shouldLogOutCloseTab = getData(
    generateOriginBasedKey(
      Functionalities.ShouldLogOutAndCloseTabAfterSmartScanCreate
    )
  );

  return shouldLogOutCloseTab;
};

export const getShouldLogOutAndCloseTabAfterPhoneCallRequestValue = () => {
  const shouldLogOutCloseTab = getData(
    generateOriginBasedKey(
      Functionalities.ShouldLogOutAndCloseTabAfterPhoneCallRequest
    )
  );

  return shouldLogOutCloseTab;
};

export const hasHideNavigationBarInLocalStorage = () => {
  const shouldHideNavbar = getData(
    generateOriginBasedKey(Functionalities.ShouldHideNavbar)
  );
  return shouldHideNavbar;
};

export const setChatBoxInvisibilityInLocalStorage = (hideChatBox) => {
  if (hideChatBox) {
    let hideChatBoxKey = generateOriginBasedKey(
      Functionalities.ShouldHideChatBox
    );
    saveData(hideChatBoxKey, hideChatBox);
  }
};

export const getChatBoxInvisibilityInLocalStorage = () => {
  const shouldHideChatBox = getData(
    generateOriginBasedKey(Functionalities.ShouldHideChatBox)
  );
  return shouldHideChatBox;
};

export const getIsFromTeledentixPatientPortal = () => {
  const isFromTeledentixPatientPortal = getData(
    Functionalities.IsFromTeledentixPatientPortal
  );

  return isFromTeledentixPatientPortal ?? false;
};

const toBool = (value) => value === 'true';

export const isFromTeledentixProviderPortal = () => {
  const hasAccessToMemberDentalDotCom = toBool(
    getData(Functionalities.HasAccessToMemberDentalDotCom)
  );

  const isFromPortal = toBool(getIsFromTeledentixPatientPortal());

  return hasAccessToMemberDentalDotCom && !isFromPortal;
};

export const updateTokenData = (tokenData) => {
  let tokenDataString = JSON.stringify(tokenData);
  let pgid = getPgidFromUrl();
  if (pgid) {
    saveData(
      Functionalities.TokenDataKey + "_" + pgid + "_" + getPatientOrigin(),
      false
    );
    storeTokenData(pgid, tokenDataString);
  }
};

export const getTokenData = (pgid) => {
  if (!pgid) {
    pgid = getPgidFromUrl();
  }

  if (pgid) {
    var tdx_token = getData(
      Functionalities.TokenDataKey + "_" + pgid + "_" + getPatientOrigin()
    );
    if (tdx_token && tdx_token != null) {
      var tokenData = JSON.parse(tdx_token);
      if (
        tokenData &&
        tokenData.token &&
        tokenData.expiration &&
        moment().utc().format() <= tokenData.expiration
      ) {
        return tokenData;
      }
    }
  }

  return false;
};

export const getSelectedPatientId = () => {
  const selectedPatId = getData(
    generateOriginBasedKey(Functionalities.SelectedPatientId)
  );
  if (selectedPatId) {
    return selectedPatId;
  }

  let curreentProfileSetting = getSelectedPatientProfile();
  if (curreentProfileSetting) {
    return curreentProfileSetting.patid;
  }

  let patientInfo = getPatientInfo();
  if (patientInfo) {
    return patientInfo.patid;
  }
  return 0;
};

export const removeTokenData = (pgId) => {
  if (!pgId) {
    pgId = getPgidFromUrl();
  }

  if (pgId) {
    removeData(
      Functionalities.TokenDataKey + "_" + pgId + "_" + getPatientOrigin()
    );
    removeSelectedPatientId();
    removeSelectedPatientProfile();
    removeSelectedPgid();
    removeHasAccessToMemberDentalDotCom();
    removeIsFromTeledentixPatientPortal();
    removeShouldLogOutAndCloseTabAfterSmartScanCreate();
    removeShouldLogOutAndCloseTabAfterPhoneCallRequestValue();
  }
};

export const onBeforeSend = (xhr) => {
  var tokenData = getTokenData();
  if (tokenData && tokenData.token) {
    xhr.setRequestHeader("Authorization", "Bearer " + tokenData.token);
  } else {
    return "";
  }
};

export const isPatient = () => {
  try {
    var tokenData = getTokenData();
    if (tokenData && tokenData.user && tokenData.user.roles) {
      var roles = tokenData.user.roles;
      for (var i = 0; i < roles.length; i++) {
        if (
          roles[i].hasOwnProperty("name") &&
          roles[i].name.toLowerCase() == "patient"
        ) {
          return true;
        }
      }
    }
  } catch (ex) { }
  return false;
};

export const isMetlifePatient = () => {
  try {
    return (
      getPatientOrigin() ===
      window?.appSettings?.metlifePatientOrigin
    );
  } catch {
    return false;
  }
};

export const getPatientInfo = () => {
  if (isPatient()) {
    var tokenData = getTokenData();
    if (tokenData && tokenData.info) {
      return tokenData.info;
    }
  }
  return false;
};

export const getPatientLoginUrl = () => {
  const tokenData = getTokenData();
  return tokenData?.info?.loginUrl;
};

export const storeSelectedPatientProfile = (selectedPatientDataString) => {
  if (selectedPatientDataString) {
    saveData(
      generateOriginBasedKey(Functionalities.SelectedPatientProfileKey),
      selectedPatientDataString
    );
    storeSelectedPatientId(JSON.parse(selectedPatientDataString).patid);
  }
};

export const getSelectedPatientProfile = () => {
  const selectedPatient = getData(
    generateOriginBasedKey(Functionalities.SelectedPatientProfileKey)
  );
  if (selectedPatient && selectedPatient != null) {
    let selectedPatientData = JSON.parse(selectedPatient);
    if (selectedPatientData) {
      return selectedPatientData;
    }
  }
  return false;
};

export const removeSelectedPatientProfile = () => {
  removeData(generateOriginBasedKey(Functionalities.SelectedPatientProfileKey));
};

export const removeSelectedPatientId = () => {
  removeData(generateOriginBasedKey(Functionalities.SelectedPatientId));
};

export const removeSelectedPgid = () => {
  removeData(Functionalities.ShouldAddPgId);
};

export const removeHasAccessToMemberDentalDotCom = () => {
  removeData(Functionalities.HasAccessToMemberDentalDotCom);
};

export const removeIsFromTeledentixPatientPortal = () => {
  removeData(Functionalities.IsFromTeledentixPatientPortal);
};

export const removeShouldLogOutAndCloseTabAfterSmartScanCreate = () => {
  removeData(
    generateOriginBasedKey(
      Functionalities.ShouldLogOutAndCloseTabAfterSmartScanCreate
    )
  );
};

export const removeShouldLogOutAndCloseTabAfterPhoneCallRequestValue = () => {
  removeData(
    generateOriginBasedKey(
      Functionalities.ShouldLogOutAndCloseTabAfterPhoneCallRequest
    )
  );
};

export const generateOriginBasedKey = (key) => {
  let pgId = getPgidFromUrl();
  return key + "_" + pgId + "_" + getPatientOrigin();
};

export const getAutoLoginUrl = (params) => {
  let tokenData = getTokenData();
  if (tokenData?.token) {
    let url =
      getBaseUrl() +
      "/login?apiKey=" +
      PATIENT_PORTAL_AUTO_LOGIN_API_KEY +
      "&token=" +
      tokenData.token;

    if (params) {
      url += "&" + $.param(params);
    }

    return url;
  }
};


export const sanitizeJSONPayload = (value, key) => {
  if (typeof value === "string") {
    return DOMPurify.sanitize(value);
  }

  if (Array.isArray(value)) {
    return value.map((v) => sanitizeJSONPayload(v));
  }

  if (value && typeof value === "object") {
    const sanitized = {};

    for (const k in value) {
      sanitized[k] = sanitizeJSONPayload(value[k], k);
    }

    return sanitized;
  }

  return value;
};


export const sanitizeFormDataPayload = (formData) => {
  if (!(formData instanceof FormData)) {
    return formData;
  }

  const sanitizedFormData = new FormData();

  for (const [key, value] of formData.entries()) {

    if (value instanceof File || value instanceof Blob) {
      sanitizedFormData.append(key, value);
    }
    else if (typeof value === "string") {
      sanitizedFormData.append(key, DOMPurify.sanitize(value));
    }
    else {
      sanitizedFormData.append(key, value);
    }
  }

  return sanitizedFormData;
};

export const sanitizeInput = (text) => {
  if (!text || text.trim().length === 0) {
    return true;
  }

  const sanitized = DOMPurify.sanitize(text);

  if (sanitized !== text) {
    alert(`Contains unsafe content and cannot be sent.`);
    return false;
  }

  return true;
};

export const stripHtmlForDisplay = (text) => {
  if (text == null || text === "") {
    return text;
  }

  return DOMPurify.sanitize(String(text), {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true
  });
};

export const sanitizeRichHtmlForDisplay = (html) => {
  if (html == null || html === "") {
    return html;
  }

  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS: [
      "p",
      "br",
      "b",
      "i",
      "em",
      "strong",
      "ul",
      "ol",
      "li",
      "span",
      "a",
      "u",
      "img",
      "iframe"
    ],
    ALLOWED_ATTR: [
      "href",
      "target",
      "rel",
      "class",
      "src",
      "alt",
      "style",
      "width",
      "height",
      "frameborder",
      "allowfullscreen"
    ],
    ALLOW_DATA_ATTR: false
  });
};
